import { memo, useEffect, useState } from "react";

import { changeCurrentPasswordAPI } from "services/api/users/api";
import { useHandleTextFieldChange } from "services/forms/hooks";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";

import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { TextField } from "ui/components/TextField/TextField";
import { useTranslate } from "helpers";

import { PasswordEditCardProps, PasswordEditFormValues } from "./types";
import { PASSWORD_EDIT_FORM_SCHEMA } from "./validation";

const initialPasswordEditForm: PasswordEditFormValues = {
  password: null,
  confirmPassword: null,
};

const PasswordEditCard: React.FC<PasswordEditCardProps> = (props) => {
  const { show, onSuccess, onClose } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<PasswordEditFormValues>(
    initialPasswordEditForm
  );
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (show) {
      setFormValues(initialPasswordEditForm);
      setErrors({});
    }
  }, [show]);

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      PASSWORD_EDIT_FORM_SCHEMA,
      setErrors
    );
    if (!isValid) return;

    setIsLoading(true);
    try {
      await changeCurrentPasswordAPI(formValues.password!);
      if (onSuccess) onSuccess();
    } catch {
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  return (
    <Modal show={show} onHide={onClose} position="centered" size="small">
      <ModalHeader
        title="Promjena lozinke"
        icon="/media/images/add-icon.svg"
        onHide={onClose}
      />
      <ModalBody>
        <div className="flex gap-4">
          <div className="w-full m-auto">
            <div className="flex gap-3 h-full">
              <form noValidate className="text-left flex-1 p-4">
                <div className="flex gap-3 mb-3">
                  <div className="flex-1">
                    <TextField
                      type="password"
                      name="password"
                      label="Nova lozinka"
                      placeholder="Unesite novu lozinku"
                      onChange={handleTextFieldChange}
                      error={errors.password}
                      value={formValues.password}
                      required
                    />
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1">
                    <TextField
                      type="password"
                      name="confirmPassword"
                      label="Potvrda lozinke"
                      placeholder="Ponovite novu lozinku"
                      onChange={handleTextFieldChange}
                      error={errors.confirmPassword}
                      value={formValues.confirmPassword}
                      required
                    />
                  </div>
                </div>
                <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                  <Button
                    variant="outlined"
                    children={t("admin.close")}
                    onClick={onClose}
                  />
                  <Button
                    variant="primary"
                    children={t("admin.save")}
                    onClick={handleFormSubmit}
                    disabled={isLoading}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>

        <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
    </Modal>
  );
};

export default memo(PasswordEditCard);
